import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useContext, useMemo } from "react";
import { truncateEthAddress } from "../utils/string";
import { Button } from "./Button";
import { Activity } from "../utils/hooks/mining";
import { AppContext } from "../context/AppContext";
import { Typed, ethers } from "ethers";
import { EMETH_CORE_CONTRACT_ADDRESS } from "../constants";
import EMETH_CORE_ABI from "../constants/abi/emethCore.json";

interface TableProps {
  tableData: Activity[];
  status: boolean;
}

function ActivityTable({ tableData, status }: TableProps) {
  const { accountData } = useContext(AppContext);

  const data = useMemo(() => {
    return Array.isArray(tableData) ? tableData : [];
  }, [tableData]);

  const handleSubmit = useCallback(
    async (jobId: string, result: string) => {
      if (!accountData) {
        return;
      }
      if (!accountData.provider) {
        return;
      }
      if (!accountData.signer) {
        return;
      }

      const activity = tableData.find((item) => item.id === jobId);

      const contract = new ethers.Contract(
        EMETH_CORE_CONTRACT_ADDRESS,
        EMETH_CORE_ABI,
        accountData.signer
      );

      try {
        const transaction = await contract.directSubmit(
          Typed.bytes16(jobId),
          Typed.string(result),
          Typed.uint256(
            parseInt(activity?.fuelLimit ? activity?.fuelLimit : "0")
          ),
          Typed.uint256(404)
        );
        await transaction.wait();
        console.log("submitted");
      } catch (error) {
        console.log("error", error);
      }
    },
    [accountData, tableData]
  );

  const columns = [
    columnHelper.accessor("created", {
      id: "created",
      size: 700,
      minSize: 20,
      maxSize: Number.MAX_SAFE_INTEGER,
      header: () => <span className="font-bold">Time</span>,
      cell: (info) => (
        <span className="text-navy-700 text-sm font-medium">
          {info.getValue()}
        </span>
      ),
    }),

    columnHelper.accessor("id", {
      id: "id",
      size: 700,
      minSize: 20,
      maxSize: Number.MAX_SAFE_INTEGER,
      header: () => <span className="font-bold">JobId</span>,
      cell: (info) => (
        <span className="text-navy-700 text-sm font-medium">
          {truncateEthAddress(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("nonce", {
      id: "nonce",
      size: 700,
      minSize: 20,
      maxSize: Number.MAX_SAFE_INTEGER,
      header: () => <span className="font-bold">Nonce</span>,
      cell: (info) => (
        <span className="text-navy-700 text-sm font-medium">
          {truncateEthAddress(info.getValue().toString())}
        </span>
      ),
    }),

    columnHelper.accessor((row) => `${row.id}/${row.result}`, {
      id: "submit",
      size: 100,
      minSize: 20,
      maxSize: 100,
      header: () => <span className="font-bold">Action</span>,
      cell: (info) => {
        const payloads = info.getValue().split("/");
        console.log("id", payloads[0]);
        console.log("result", payloads[1]);
        return (
          <span className="text-navy-700 text-sm font-medium">
            <Button
              onClick={() => handleSubmit(payloads[0], payloads[1])}
              text="Submit"
            />
          </span>
        );
      },
    }),
  ]; // eslint-disable-next-line
  const table = useReactTable({
    data,
    columns,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });
  return (
    <div className="grid grid-cols-1 rounded-[30px]">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-[#1C1C1C]">
                {headerGroup.headers.map((header) => {
                  const { size, maxSize, minSize } = header.column.columnDef;
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer border-b-[1px] border-gray-200 p-2 text-start text-white"
                      style={{
                        width: size,
                        minWidth: minSize,
                        maxWidth: maxSize,
                      }}
                    >
                      <div className="items-center justify-between">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white">
            {table
              .getRowModel()
              .rows.slice(0, 6)
              .map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id} className=" min-w-[150px] py-3 pr-4">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
        {status && (
          <div className="flex flex-col justify-center items-center pt-4">
            <div
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityTable;
const columnHelper = createColumnHelper<Activity>();
